import { Link } from "react-router-dom";
import { AlertTriangle, Clock, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, waLink } from "@/lib/api";
import { cn } from "@/lib/utils";

export function TrialBanner() {
  const { session } = useAuth();
  const sub = session?.subscription;
  if (!sub || sub.status === "active") return null;
  const blocked = !sub.can_sell;
  const urgent = sub.status === "trial" && sub.days_left <= 7;
  const text =
    sub.status === "trial"
      ? `${sub.days_left} dia${sub.days_left === 1 ? "" : "s"} de teste grátis restante${sub.days_left === 1 ? "" : "s"}`
      : sub.alert;
  return (
    <div
      data-testid="trial-countdown-banner"
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm border-b",
        blocked ? "bg-red-500/10 text-red-500 border-red-500/20" : urgent ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" : "bg-primary/10 text-primary border-primary/20"
      )}
    >
      <span className="flex items-center gap-2 font-medium">
        {blocked ? <Lock className="h-4 w-4" /> : urgent ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
        {text}
        {sub.alert && sub.status === "trial" && <span className="hidden md:inline opacity-80">— {sub.alert}</span>}
      </span>
      <Button asChild size="sm" variant={blocked ? "destructive" : "default"} className="h-8" data-testid="trial-banner-choose-plan-btn">
        <Link to="/app/planos">{blocked ? "Assinar agora" : "Escolher plano"}</Link>
      </Button>
    </div>
  );
}

export function BlockWarningBanner() {
  const { session } = useAuth();
  const w = session?.block_warning;
  if (!w) return null;
  const when = fmtDateTime(w.at);
  const text = w.kind === "overdue"
    ? `Pagamento pendente: renove sua assinatura para evitar o bloqueio automático do sistema em ${when}.`
    : `Atenção: o acesso ao sistema será bloqueado em ${when}.`;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-red-500/20 bg-red-500/10 px-4 py-2 text-sm text-red-500" data-testid="block-warning-banner">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1 font-medium">{text} Fale com o suporte para regularizar.</span>
      {w.support_whatsapp && <Button asChild size="sm" variant="outline" className="h-8 bg-transparent"><a href={waLink(w.support_whatsapp, "Olá! Recebi um aviso de bloqueio no sistema.")} target="_blank" rel="noreferrer" data-testid="block-warning-whatsapp">WhatsApp</a></Button>}
      {w.support_email && <Button asChild size="sm" variant="outline" className="h-8 bg-transparent"><a href={`mailto:${w.support_email}`} data-testid="block-warning-email">E-mail</a></Button>}
      {w.kind === "overdue" && <Button asChild size="sm" variant="destructive" className="h-8"><Link to="/app/planos" data-testid="block-warning-plans">Renovar agora</Link></Button>}
    </div>
  );
}

export function SellBlocked() {
  const { session } = useAuth();
  if (session?.subscription?.can_sell !== false) return null;
  return (
    <div className="card-surface border-red-500/40 bg-red-500/5 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="sell-blocked-notice">
      <Lock className="h-8 w-8 text-red-500 shrink-0" />
      <div className="flex-1">
        <p className="font-display font-semibold">Novas vendas bloqueadas</p>
        <p className="text-sm text-muted-foreground">{session.subscription.alert} Seus dados continuam salvos.</p>
      </div>
      <Button asChild data-testid="sell-blocked-plans-btn"><Link to="/app/planos">Ver planos</Link></Button>
    </div>
  );
}
